import {
  FormControlLabel as NovaFormControlLabel,
  FormControlLabelProps,
  Radio as NovaBaseRadio,
  RadioGroup as NovaBaseRadioGroup,
} from '@fme-nova/components'
import { default as MuiFormControlLabel } from '@mui/material/FormControlLabel'
import { default as MuiRadio, RadioProps } from '@mui/material/Radio'
import { default as MuiRadioGroup, RadioGroupProps } from '@mui/material/RadioGroup'
import React from 'react'

import { styled, uiTokens } from '../../theme'
import { createComponentWrapperWithRef } from '../../theme'
import { COLORS } from '../../theme/colors'
import { computeStatusPropsFromOptionItem, MsdkStyledComponent, OptionItem } from '../../utils'
import { Status } from '../Status'
import { RadioGroupDirection } from './RadioGroupDirection'

type StyledRadioProps = Omit<RadioProps, 'ref'>
type StyledRadioGroupProps = Omit<RadioGroupProps, 'ref'>

const MuiStyledRadioButton: MsdkStyledComponent<StyledRadioProps> = styled(MuiRadio)`
  color: ${COLORS.text.secondary};

  &:hover {
    background-color: ${COLORS.primary.hover};
  }

  &:disabled {
    background-color: ${COLORS.text.disabled};
  }

  &.Mui-checked {
    color: ${COLORS.primary.main};
  }
`

const NovaStyledRadioButton: MsdkStyledComponent<StyledRadioProps> = styled(NovaBaseRadio)`
  color: ${uiTokens.uiControl.contained.enabled};

  &:hover {
    background-color: ${COLORS.primary.hover};
  }

  &:disabled {
    background-color: ${COLORS.text.disabled};
  }

  &.Mui-checked {
    color: ${COLORS.primary.main};
  }
`

export const StyledRadioButton = createComponentWrapperWithRef<StyledRadioProps>(
  {
    mui: MuiStyledRadioButton,
    nova: NovaStyledRadioButton,
  },
  'StyledRadioButton',
)

export const Radio = StyledRadioButton

const MuiStyledRadioGroup: MsdkStyledComponent<StyledRadioGroupProps> = styled(MuiRadioGroup)`
  color: ${COLORS.text.primary};

  &:disabled {
    color: ${COLORS.text.disabled};
  }
  /* Fix: reset cursor on cell, restore it only on interactive elements */
  cursor: default;

  .MuiFormControlLabel-root {
    width: fit-content;
  }

  .MuiFormControlLabel-root .MuiButtonBase-root,
  .MuiFormControlLabel-root .MuiFormControlLabel-label {
    cursor: pointer;
  }
`

const NovaStyledRadioGroup: MsdkStyledComponent<StyledRadioGroupProps> = styled(NovaBaseRadioGroup)`
  color: ${COLORS.text.primary};

  &:disabled {
    color: ${COLORS.text.disabled};
  }
`

export const StyledRadioGroup = createComponentWrapperWithRef<StyledRadioGroupProps>(
  {
    mui: MuiStyledRadioGroup,
    nova: NovaStyledRadioGroup,
  },
  'StyledRadioGroup',
)

const FormControlLabel = createComponentWrapperWithRef<FormControlLabelProps>(
  {
    mui: MuiFormControlLabel,
    nova: NovaFormControlLabel,
  },
  'FormControlLabel',
)

export const BaseRadioGroup = StyledRadioGroup

const OptionWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 4px;
`

export function RadioGroup(props: {
  options: OptionItem[]
  value: OptionItem[]
  onChange: (value: string | string[]) => void
  direction?: RadioGroupDirection
  disabled?: boolean
}) {
  function handleChange(opt: string) {
    const el = props.value.find((f) => f.value === opt)
    if (el) {
      props.onChange([])
    } else props.onChange([opt])
  }

  function checkValue(e: OptionItem): boolean {
    const r = props.value.findIndex((el) => el.value === e.value)
    return r !== -1
  }

  return (
    <>
      <StyledRadioGroup row={props.direction !== RadioGroupDirection.Vertical} value={props.value}>
        {props.options.map((e, i) => {
          const statusProps = computeStatusPropsFromOptionItem({ fontColor: e.color, icon: e.icon })

          return (
            <FormControlLabel
              key={i}
              control={
                <StyledRadioButton
                  data-test-id="radio-button"
                  data-test-index={i}
                  checked={checkValue(e)}
                  value={e.value}
                  onClick={() => handleChange(e.value)}
                />
              }
              label={<Status {...statusProps}>{e.label}</Status>}
              disabled={props.disabled}
            />
          )
        })}
      </StyledRadioGroup>
    </>
  )
}
